'use client';

import Image from 'next/image';
import { User } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Mail, Phone, BookOpen, Calendar, Shield, User as UserIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserCardProps {
  user: User;
  onEdit?: (user: User) => void;
  onView?: (user: User) => void;
  view?: 'grid' | 'list';
}

const roleConfig = {
  admin: { label: '管理员', color: 'bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/30' },
  librarian: { label: '图书管理员', color: 'bg-[#64FFDA]/20 text-[#64FFDA] border-[#64FFDA]/30' },
  reader: { label: '读者', color: 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30' },
};

const statusConfig = {
  active: { label: '正常', color: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
  inactive: { label: '未激活', color: 'bg-[#8892B0]/20 text-[#8892B0] border-[#8892B0]/30' },
  suspended: { label: '已暂停', color: 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30' },
};

export function UserCard({ user, onEdit, onView, view = 'grid' }: UserCardProps) {
  const role = roleConfig[user.role];
  const status = statusConfig[user.status];

  if (view === 'list') {
    return (
      <div className="flex items-center gap-4 p-4 rounded-lg bg-[#112240] border border-[#1d3557] hover:border-[#64FFDA]/30 transition-all">
        <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[#64FFDA]/30">
          <Image
            src={user.avatar}
            alt={user.name}
            fill
            className="object-cover"
            crossOrigin="anonymous"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-[#E6F1FF]">{user.name}</h3>
            <Badge className={cn("text-xs", role.color)}>{role.label}</Badge>
            <Badge className={cn("text-xs", status.color)}>{status.label}</Badge>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-[#8892B0]">
            <span className="flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" />
              {user.email}
            </span>
            <span className="flex items-center gap-1">
              <Phone className="w-3.5 h-3.5" />
              {user.phone}
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" />
              {user.borrowedBooks}/{user.maxBooks} 本
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-[#1d3557] text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#1d3557]"
            onClick={() => onView?.(user)}
          >
            查看
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-[#1d3557] text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#1d3557]"
            onClick={() => onEdit?.(user)}
          >
            编辑
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 rounded-xl bg-[#112240] border border-[#1d3557] card-hover">
      <div className="flex items-start gap-4">
        <div className="relative w-16 h-16 rounded-full overflow-hidden ring-2 ring-[#64FFDA]/30 flex-shrink-0">
          <Image
            src={user.avatar}
            alt={user.name}
            fill
            className="object-cover"
            crossOrigin="anonymous"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-[#E6F1FF]">{user.name}</h3>
          <p className="text-sm text-[#8892B0]">{user.email}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge className={cn("text-xs", role.color)}>{role.label}</Badge>
            <Badge className={cn("text-xs", status.color)}>{status.label}</Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-[#1d3557]">
        <div className="flex items-center gap-2 text-sm text-[#8892B0]">
          <Phone className="w-4 h-4" />
          <span className="truncate">{user.phone}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#8892B0]">
          <BookOpen className="w-4 h-4" />
          <span>{user.borrowedBooks}/{user.maxBooks} 本</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#8892B0]">
          <Calendar className="w-4 h-4" />
          <span>加入: {user.joinDate}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#8892B0]">
          <Shield className="w-4 h-4" />
          <span>到期: {user.expiryDate}</span>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 border-[#1d3557] text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#1d3557]"
          onClick={() => onView?.(user)}
        >
          <UserIcon className="w-4 h-4 mr-1" />
          查看
        </Button>
        <Button
          size="sm"
          className="flex-1 bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90"
          onClick={() => onEdit?.(user)}
        >
          编辑
        </Button>
      </div>
    </div>
  );
}
